import { useCallback, useRef, useState } from "react";

type FeedbackState = "idle" | "success" | "error";

const FEEDBACK_CLASS: Record<FeedbackState, string> = {
  idle: "",
  success: "tx-feedback-success",
  error: "tx-feedback-error",
};

const DURATION_MS: Record<Exclude<FeedbackState, "idle">, number> = {
  success: 3500,
  error: 2800,
};

/**
 * Provides a CSS class that briefly flashes the container border
 * green (success) or red (error) after a transaction completes.
 *
 * Usage:
 *   const { feedbackClass, onTxSuccess, onTxError } = useTxFeedback();
 *   <Glass className={cn("glass-card", feedbackClass)}>
 */
export function useTxFeedback(): {
  feedbackClass: string;
  onTxSuccess: () => void;
  onTxError: () => void;
} {
  const [state, setState] = useState<FeedbackState>("idle");
  const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const trigger = useCallback((kind: "success" | "error") => {
    clearTimeout(timerRef.current);
    // Reset to idle first so re-triggering restarts the animation
    setState("idle");
    requestAnimationFrame(() => {
      setState(kind);
      timerRef.current = setTimeout(() => {
        setState("idle");
      }, DURATION_MS[kind]);
    });
  }, []);

  const onTxSuccess = useCallback(() => {
    trigger("success");
  }, [trigger]);
  const onTxError = useCallback(() => {
    trigger("error");
  }, [trigger]);

  return { feedbackClass: FEEDBACK_CLASS[state], onTxSuccess, onTxError };
}
